import {
  Bell,
  Clock,
  GitBranch,
  Key,
  Mail,
  MessageSquare,
  Search as SearchIcon,
  Settings,
  Shield,
  Webhook,
} from 'lucide-react';

/** Internal backend keys hidden from normal/basic picker mode. */
export const HIDDEN_BACKEND_ACTION_KEYS = new Set([
  'find_user_by_email',
  'resolve_user_portal',
  'generate_secure_reset_token',
  'build_reset_link',
  'record_delivery_attempt',
  'on_password_reset_completed',
  'on_token_expiration',
  'log_security_events',
  'normalize_auth_email',
  'generate_password_reset_token',
  'store_hashed_reset_token',
  'password_reset_generic_response',
  'mark_password_reset_token_used',
]);

export const PREPARE_PASSWORD_RESET_SEQUENCE = [
  'find_user_by_email',
  'resolve_user_portal',
  'generate_secure_reset_token',
  'build_reset_link',
];

export const FIND_USER_PORTAL_SEQUENCE = [
  'find_user_by_email',
  'resolve_user_portal',
];

export const ACTION_KEY_ALIASES = {
  generate_password_reset_token: 'generate_secure_reset_token',
  store_hashed_reset_token: 'generate_secure_reset_token',
  mark_password_reset_token_used: 'on_password_reset_completed',
};

export function normalizeBackendActionKey(key) {
  const raw = String(key || '').trim().toLowerCase();
  if (!raw) return '';
  return ACTION_KEY_ALIASES[raw] || raw;
}

export function getStepBackendActionKey(step) {
  if (!step) return '';
  const type = String(step.type || '').toLowerCase();
  if (type !== 'backend_action' && type !== 'action') {
    if (step.config?.action_key) return normalizeBackendActionKey(step.config.action_key);
    return '';
  }
  return normalizeBackendActionKey(step.config?.action_key || step.config?.actionKey || step.action_key);
}

export const WORKFLOW_ACTION_CATALOG = [
  {
    id: 'core',
    name: 'Core Actions',
    actions: [
      {
        id: 'send_email',
        name: 'Send Email',
        description: 'Send an email to the contact',
        icon: Mail,
        color: 'emerald',
        stepType: 'send_email',
        keywords: ['email', 'send', 'message', 'mail'],
      },
      {
        id: 'send_sms',
        name: 'Send SMS',
        description: 'Send an SMS message',
        icon: MessageSquare,
        color: 'emerald',
        stepType: 'send_sms',
        keywords: ['sms', 'text', 'message'],
      },
      {
        id: 'send_notification',
        name: 'Internal Notification',
        description: 'Notify staff member',
        icon: Bell,
        color: 'emerald',
        stepType: 'send_notification',
        keywords: ['notification', 'alert', 'staff', 'internal'],
      },
      {
        id: 'wait',
        name: 'Wait',
        description: 'Wait for a specified duration or event',
        icon: Clock,
        color: 'amber',
        stepType: 'wait',
        keywords: ['wait', 'delay', 'pause', 'wait until', 'timeout', 'until event'],
      },
      {
        id: 'condition',
        name: 'If / Else',
        description: 'Branch based on conditions',
        icon: GitBranch,
        color: 'purple',
        stepType: 'if_else',
        keywords: ['if', 'else', 'condition', 'branch', 'logic'],
      },
    ],
  },
  {
    id: 'data_tools',
    name: 'Data Tools',
    advancedOnly: true,
    actions: [
      {
        id: 'find_user_portal',
        name: 'Find User & Portal',
        description: 'Lookup user by email and resolve the correct portal host',
        icon: SearchIcon,
        color: 'emerald',
        groupType: 'find_user_portal',
        badge: 'Data Tool',
        keywords: [
          'find user',
          'lookup user',
          'email lookup',
          'resolve portal',
          'portal host',
          'reset host',
          'user portal',
          'data tool',
          'contact lookup',
          'user lookup',
          'portal',
        ],
        internalMappings: ['find_user_by_email', 'resolve_user_portal'],
      },
    ],
  },
  {
    id: 'system',
    name: 'System Actions',
    actions: [
      {
        id: 'prepare_password_reset',
        name: 'Prepare Password Reset',
        description: 'Find user, resolve portal, and generate secure reset link',
        icon: Key,
        color: 'emerald',
        groupType: 'prepare_password_reset',
        badge: 'System',
        attentionBadge: 'Backend required',
        keywords: [
          'password reset',
          'forgot password',
          'reset link',
          'token',
          'generate token',
          'build reset link',
          'find user',
          'resolve portal',
          'authentication',
          'portal user',
          'send reset email',
          'secure reset',
          'record delivery',
        ],
        internalMappings: PREPARE_PASSWORD_RESET_SEQUENCE,
      },
      {
        id: 'grant_portal_access',
        name: 'User Portal Access',
        description: 'Grant or manage portal access',
        icon: Shield,
        color: 'emerald',
        stepType: 'grant_portal_access',
        keywords: ['portal', 'access', 'grant', 'user portal'],
      },
      {
        id: 'audit_log',
        name: 'Audit Log',
        description: 'Record a security or audit event',
        icon: Settings,
        color: 'slate',
        stepType: 'audit_log',
        keywords: ['audit', 'log', 'security', 'record delivery'],
      },
    ],
  },
  {
    id: 'advanced',
    name: 'Advanced',
    permissionRequired: true,
    actions: [
      {
        id: 'backend_action',
        name: 'Backend Action',
        description: 'Advanced internal system operation',
        icon: Settings,
        color: 'orange',
        stepType: 'backend_action',
        keywords: ['backend', 'internal', 'advanced', 'system operation', 'developer action'],
      },
      {
        id: 'webhook',
        name: 'Webhook / API',
        description: 'Call an external webhook URL',
        icon: Webhook,
        color: 'slate',
        stepType: 'webhook',
        keywords: ['webhook', 'api', 'http', 'external'],
      },
    ],
  },
];

export function getCatalogContext(options = {}) {
  const {
    advancedMode = false,
    allowAdvancedActions = false,
    allowDataTools = false,
  } = options;

  return {
    advancedMode,
    allowAdvancedActions: advancedMode || allowAdvancedActions,
    allowDataTools: advancedMode || allowDataTools,
  };
}

export function isCatalogActionVisible(action, group, context) {
  if (action.advancedOnly && !context.allowDataTools) return false;
  if (group.advancedOnly && !context.allowDataTools) return false;
  if (group.permissionRequired && !context.allowAdvancedActions) return false;
  if (action.permissionRequired && !context.allowAdvancedActions) return false;
  return true;
}

export function getVisibleCatalogGroups(context) {
  return WORKFLOW_ACTION_CATALOG
    .map((group) => ({
      ...group,
      actions: group.actions.filter((action) => isCatalogActionVisible(action, group, context)),
    }))
    .filter((group) => group.actions.length > 0);
}

function actionMatchesQuery(action, query) {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (action.name.toLowerCase().includes(q)) return true;
  if (action.description?.toLowerCase().includes(q)) return true;
  if (action.keywords?.some((kw) => kw.toLowerCase().includes(q) || q.includes(kw.toLowerCase()))) {
    return true;
  }
  if (action.internalMappings?.some((key) => key.replace(/_/g, ' ').includes(q))) return true;
  return false;
}

/** Search mapping: internal keys map to parent actions in normal mode. */
export function resolveSearchTargets(query, context) {
  const q = query.toLowerCase().trim();
  if (!q) return null;

  const allActions = WORKFLOW_ACTION_CATALOG.flatMap((g) => g.actions);
  const visible = allActions.filter((action) => {
    const group = WORKFLOW_ACTION_CATALOG.find((g) => g.actions.includes(action));
    return isCatalogActionVisible(action, group, context);
  });

  const findVisible = (actionId) => visible.find((action) => action.id === actionId) || null;

  if (q.includes('password reset completed')) {
    const wait = findVisible('wait');
    if (wait) return [wait];
  }
  if (q.includes('token expiration')) {
    const wait = findVisible('wait');
    if (wait) return [wait];
  }

  const priorityMappings = [
    ['forgot password', 'prepare_password_reset'],
    ['password reset', 'prepare_password_reset'],
    ['reset link', 'prepare_password_reset'],
    ['generate token', 'prepare_password_reset'],
    ['build reset link', 'prepare_password_reset'],
    ['send reset email', 'prepare_password_reset'],
    ['secure reset', 'prepare_password_reset'],
    ['token', 'prepare_password_reset'],
    ['find user', context.allowDataTools ? 'find_user_portal' : null],
    ['lookup user', context.allowDataTools ? 'find_user_portal' : null],
    ['resolve portal', context.allowDataTools ? 'find_user_portal' : null],
    ['portal host', context.allowDataTools ? 'find_user_portal' : null],
    ['reset host', context.allowDataTools ? 'find_user_portal' : null],
    ['user portal', context.allowDataTools ? 'find_user_portal' : null],
    ['data tool', context.allowDataTools ? 'find_user_portal' : null],
    ['backend', context.allowAdvancedActions ? 'backend_action' : null],
  ];

  for (const [term, actionId] of priorityMappings) {
    if (!actionId) continue;
    if (q === term || q.includes(term)) {
      const match = findVisible(actionId);
      if (match) return [match];
    }
  }

  const direct = visible.filter((action) => actionMatchesQuery(action, q));
  if (direct.length) return direct;

  return [];
}

export function filterCatalogGroups(groups, query, context) {
  const trimmed = query.trim();
  if (!trimmed) return groups;

  const searchTargets = resolveSearchTargets(trimmed, context);
  if (searchTargets?.length) {
    const ids = new Set(searchTargets.map((a) => a.id));
    return groups
      .map((group) => ({
        ...group,
        actions: group.actions.filter((action) => ids.has(action.id)),
      }))
      .filter((group) => group.actions.length > 0);
  }

  return groups
    .map((group) => ({
      ...group,
      actions: group.actions.filter((action) => actionMatchesQuery(action, trimmed)),
    }))
    .filter((group) => group.actions.length > 0);
}

export function findCatalogAction(actionId) {
  for (const group of WORKFLOW_ACTION_CATALOG) {
    const action = group.actions.find((item) => item.id === actionId);
    if (action) return action;
  }
  return null;
}

export function isHiddenBackendStep(step, context) {
  const key = getStepBackendActionKey(step);
  if (!key) return false;
  if (HIDDEN_BACKEND_ACTION_KEYS.has(key)) {
    return !context.advancedMode;
  }
  return false;
}
